import Spinner from '@carvana/ds-spinner';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { EventedModal, ModalNames } from '|/app/modals';
import { currency } from '|/helpers';
import { getPaymentMethodSummary, type PaymentMethodSummary } from '|/services/purchase-payments';
import styles from './credit-applied-modal.module.css';
import {
  buildCreditModalRows,
  getPaymentInitiatedText,
  getPaymentStatusDisplayText,
  getPaymentStatusKind
} from './helpers';

const modalHeader = 'Credit applied';
const creditLabel = 'Total credit';
const errorText = 'Unable to load credit details. Please try again later.';

interface CreditAppliedModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreditAppliedModal = ({ isOpen, onClose }: CreditAppliedModalProps) => {
  const [data, setData] = useState<PaymentMethodSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    []
  );

  const fetchData = useCallback(async () => {
    if (data !== null || isLoading) return;
    setIsLoading(true);
    setHasError(false);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const result = await getPaymentMethodSummary(controller.signal);
      if (!controller.signal.aborted) {
        setData(result);
      }
    } catch {
      if (!controller.signal.aborted) {
        setHasError(true);
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [data, isLoading]);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, fetchData]);

  const renderModalBody = () => {
    if (isLoading) {
      return (
        <div className={styles.loading}>
          <Spinner size={48} />
        </div>
      );
    }

    if (hasError || !data) {
      return <div className={styles.error}>{errorText}</div>;
    }

    const rows = buildCreditModalRows(data);
    const total = data.purchasePaymentBreakdown.credit;

    return (
      <>
        <div className={styles.header}>
          <span>{creditLabel}</span>
          <span>{total > 0 ? '-' + currency(total) : currency(total)}</span>
        </div>

        <div className={styles.body}>
          {rows.map(row => {
            const statusKind = row.payment ? getPaymentStatusKind(row.payment.status) : null;
            const statusClassName =
              statusKind === 'succeeded'
                ? styles.succeeded
                : statusKind === 'processing'
                  ? styles.processing
                  : '';
            return (
              <div className={styles.paymentCreditContainer} key={row.key}>
                <div className={styles.paymentCreditTopLine}>
                  <span>{row.label}</span>
                  <span>{row.isCredit ? '-' + currency(row.amount) : currency(row.amount)}</span>
                </div>
                {row.payment && (
                  <div className={styles.paymentCreditBottomLine}>
                    <span>
                      {getPaymentInitiatedText(row.payment.paymentMethod, row.payment.createdOnUtc)}
                    </span>
                    <span className={statusClassName}>
                      {getPaymentStatusDisplayText(row.payment.status)}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <EventedModal
      analyticsId={ModalNames.CreditApplied}
      isOpen={isOpen}
      onClose={onClose}
      modalHeader={modalHeader}
      closeIcon
      divLine
      mobileStyle="inset"
      contentPadding={false}
    >
      <div className={styles.container}>{renderModalBody()}</div>
    </EventedModal>
  );
};

export default CreditAppliedModal;
